import type { ReactNode } from "react";
import { parseTemplate } from "./parseTemplate";
import { SLOT_REGISTRY } from "./slotRegistry";
import { SCOPE_ROOT_SELECTOR } from "./scopeRoot";
import { templateParseRoot } from "./templateParseRoot";

/**
 * Every `<Templated id="lastbot-…">` shell the widget knows about. The widget
 * mounts these as React components; an embedder's `<template id="X">` only
 * has an effect when `X` matches one of these ids. Anything else is
 * silently ignored at render time, but its slot claims would still
 * propagate through `slotClaimMap` and could suppress real default slots
 * via drop-on-override — so we warn about it here at discovery time.
 *
 * Two id families are accepted:
 *  - Fixed shell ids — the page / component templates listed below, plus
 *    `lastbot-app` (top-level shell wrapper).
 *  - Per-slot ids of the form `lastbot-<slotId>` for any slot in the slot
 *    registry. Every `<Slot>` is implicitly wrapped in a
 *    `<Templated id="lastbot-${slotName}">`, so an embedder declaring
 *    `<template id="lastbot-assistant-avatar">` overrides just that
 *    slot's content without restructuring its container. See `Slot.tsx`
 *    and `Templated.tsx` (SlotPlaceholder branch) for the rendering side.
 */
const FIXED_TEMPLATE_IDS: ReadonlySet<string> = new Set([
  // Top-level shell.
  "lastbot-app",
  // Per-component / per-page shells.
  "lastbot-toggle-button",
  "lastbot-navbar",
  "lastbot-landing-page",
  "lastbot-customer-inquiry-page",
  "lastbot-conversation-page",
  "lastbot-chat-history-page",
  "lastbot-login-page",
  "lastbot-enter-code-page",
  "lastbot-error-page",
  "lastbot-custom-main-page",
  "lastbot-message-list",
  "lastbot-message-bubble-user",
  "lastbot-message-bubble-assistant",
  "lastbot-typing-indicator",
  "lastbot-attachment-chip",
  "lastbot-composer-text",
  "lastbot-composer-voice",
  "lastbot-support-agent-status",
  "lastbot-rating-display",
]);

/**
 * True when a `lastbot-<slotId>` template id corresponds to a shell
 * component that wraps itself in `<Templated id="lastbot-<slotId>">` —
 * i.e. the slot is rendered by Navbar, ChatMessageList, ChatFormText, etc.,
 * not by raw JSX. The Slot's per-slot Templated wrap must skip these to
 * avoid (a) double `data-slot-name` markers and (b) intercepting the
 * embedder template at the wrong layer (the outer Slot harvest can't see
 * inside the shell component's React tree, so its harvest comes back
 * empty and the embedder's `<slot name="…">` placeholders get nothing).
 *
 * The check is by template id, not slot kind — `composer-text` and
 * `composer-voice` are `interactive` kind but have shells; the kind alone
 * is not enough.
 */
export const hasShellTemplate = (slotId: string): boolean =>
  FIXED_TEMPLATE_IDS.has(`lastbot-${slotId}`);

/**
 * The scope root that owns a template, or null if it is global. Ownership = nearest
 * `[data-lastbot]`/`<lastbot-chat>` ancestor. This scopes each instance to its own
 * templates and excludes any nested scope root's templates.
 */
const templateOwner = (el: Element): Element | null => el.closest(SCOPE_ROOT_SELECTOR);

/**
 * Selector for every embedder template declaration, in either equivalent form:
 *  - `<template id="lastbot-navbar">` — the id form; also the document-global channel.
 *  - `<template data-lastbot-template="navbar">` — the id-less form. Carries no `id`, so
 *    the SAME template can be declared on multiple placements of one page without emitting
 *    duplicate document IDs (invalid HTML). Preferred for per-placement scoped templates.
 */
const TEMPLATE_SELECTOR = 'template[id^="lastbot-"], template[data-lastbot-template]';

/**
 * The canonical `lastbot-<slotId>` key an embedder template maps to, or null if it declares
 * neither form. The id-less `data-lastbot-template="navbar"` form maps to `lastbot-navbar`,
 * so downstream consumers (`<Templated>` / `<Slot>`) look templates up by the same key
 * regardless of how they were declared. The data-attribute form wins when both are present
 * on one element (it's the intended, id-safe declaration).
 */
const templateKeyOf = (el: HTMLTemplateElement): string | null => {
  const name = el.dataset.lastbotTemplate;
  if (name != null && name !== "") return `lastbot-${name}`;
  if (el.id.startsWith("lastbot-")) return el.id;
  return null;
};

/**
 * True iff `container` (a scope root — `[data-lastbot]` or `<lastbot-chat>`) owns at
 * least one `lastbot-*` structural template element. Presence-based (not parse-based):
 * a typo'd or parse-failing template still counts, so a scope root that declares
 * templates never silently falls back to the global set.
 *
 * The scoped styles block is an id-less `<template>` wrapping a `<style>` (see
 * loadExternalStylesheet.ts); it has neither a `lastbot-*` id nor `data-lastbot-template`,
 * so it never matches here — a scope root that carries only scoped styles still resolves
 * templates from the global set.
 */
export const containerDeclaresTemplates = (container: Element): boolean => {
  const els = container.querySelectorAll<HTMLTemplateElement>(TEMPLATE_SELECTOR);
  for (let i = 0; i < els.length; i++) {
    const el = els[i];
    if (templateOwner(el) !== container) continue; // owned by a nested container
    if (templateKeyOf(el) !== null) return true; // real declaration (id or data-attr)
  }
  return false;
};

const isKnownTemplateId = (id: string): boolean => {
  if (FIXED_TEMPLATE_IDS.has(id)) return true;
  // Per-slot template: `lastbot-<slotId>` for any registered slot.
  if (id.startsWith("lastbot-")) {
    const slotId = id.slice("lastbot-".length);
    if (Object.prototype.hasOwnProperty.call(SLOT_REGISTRY, slotId)) return true;
  }
  return false;
};

/**
 * Discover `<template id="lastbot-…">` elements owned by `root` and parse each into
 * an immutable React element tree.
 *
 *  - `root = document` (default): the global set — templates owned by NO scope root
 *    (`closest("[data-lastbot], lastbot-chat") === null`). Scope-root templates belong to
 *    a single instance and must not leak into a global fallback.
 *  - `root` = a scope root (`[data-lastbot]` or `<lastbot-chat>`): only templates it owns
 *    (`closest(SCOPE_ROOT_SELECTOR) === root`), excluding any nested scope root's templates.
 *
 * Both declaration forms are considered — `template[id^="lastbot-"]` and
 * `template[data-lastbot-template="<slot>"]` — mapped to the same canonical key. The scoped
 * styles block (an id-less `<template>` wrapping a `<style>`, with neither marker) is
 * naturally ignored here — it belongs to the styling channel (see loadExternalStylesheet.ts).
 * Templates added to the page AFTER widget init are not picked up (documented limitation).
 *
 * Unknown `lastbot-*` ids — typos like `lastbot-landnig-page` — are skipped
 * with a dev-mode warning. Without this guard, the typoed template's slot
 * claims would still flow into `slotClaimMap` and the real page's default
 * slots would silently drop via the cross-template "claimed elsewhere"
 * check. The warning lists the known ids so the embedder can fix the typo.
 */
export const discoverTemplates = (
  root: Document | Element = document,
): Map<string, ReactNode> => {
  const templates = new Map<string, ReactNode>();
  const scopeRoot: Element | null =
    root.nodeType === Node.DOCUMENT_NODE ? null : (root as Element);
  const elements = root.querySelectorAll<HTMLTemplateElement>(TEMPLATE_SELECTOR);
  for (let i = 0; i < elements.length; i++) {
    const el = elements[i];
    // Ownership gate: global scan keeps only unowned templates; a container scan
    // keeps only its own (drops nested-container templates).
    if (templateOwner(el) !== scopeRoot) continue;
    const id = templateKeyOf(el);
    if (id === null) continue; // e.g. an empty data-lastbot-template="" — nothing declared
    if (!isKnownTemplateId(id)) {
      console.warn(
        `[lastbot-chat] template "${id}" is not recognised and will be ` +
          "ignored. See docs/api.md § Templating guide for valid ids " +
          "(or call core.listSlots() for the slot half).",
      );
      continue;
    }
    if (templates.has(id)) {
      // Same slot declared twice within one root (e.g. an id form and a data-attr form, or
      // two data-attr forms). Keep the first in DOM order and flag the redundant one.
      console.warn(
        `[lastbot-chat] template "${id}" is declared more than once in the same scope; ` +
          "keeping the first and ignoring the rest.",
      );
      continue;
    }
    try {
      // Prefer .content; fall back to host childNodes for React-authored templates
      // (JSX children land on the <template> host, not .content — LAS-2116).
      templates.set(id, parseTemplate(templateParseRoot(el), id));
    } catch (err) {
      console.warn(`[lastbot-chat] failed to parse template "${id}":`, err);
    }
  }
  return templates;
};
